import { useCallback } from "react"

import {
	acriss,
	categories,
	currency,
	directSiteBrokerProvider,
	fuelPolicy,
	insurance,
	inTerminal,
	lengthRental,
	mainProvider,
	mainSite,
	mileage,
	offices,
	prepaid,
	priceKpi,
	providers,
	reports,
	searchID,
	sites,
	transmission
} from "@signals"
import { positionSchema } from "../schema"
import { useToast } from "@hooks"
import { tableHasMainComparison } from "Pages/AnalyzersV2/@signals"
import { analyzerRequest } from "Pages/AnalyzersV2/@services/analyzerRequestApp"
import { useValidatorSeeker } from "@app"
import { positionPayload } from "Pages/AnalyzersV2/Position/@signals"
import { SECTIONS } from "Pages/AnalyzersV2/@constants"


export default function useAnalyzerPositionFilters() {
	const { showToast } = useToast()
	const isProviderMode = !!directSiteBrokerProvider.value
	const { isValid, errors } = useValidatorSeeker(positionPayload.value, positionSchema(searchID.value, isProviderMode))

	// `keep` (a car picked; CU-869fmpqfm C1): the KPIs, charts and table stay under "Updating…", nothing is reset
	const onSubmit = useCallback((clearTab = false, { keep = false } = {}) => {
		tableHasMainComparison.value = !!mainSite.value || !!mainProvider.value
		// Read once, here: the toggle can be flipped while the request is in flight,
		// and the results must keep describing the mode they were asked for.
		const requestedProviderMode = !!directSiteBrokerProvider.value
		analyzerRequest.run({
			payload: positionPayload.value, type: SECTIONS.POSITION, keep, clearTab,
			extra: { isProviderMode: requestedProviderMode },
			onError: () => showToast.error(),
		})
	}, [
		currency.value, offices.value, acriss.value,
		transmission.value, categories.value, fuelPolicy.value, lengthRental.value,
		providers.value, prepaid.value, mileage.value, priceKpi.value, inTerminal.value,
		insurance.value, sites.value, reports.value, isValid,
		directSiteBrokerProvider.value, mainSite.value, mainProvider.value
	])

	// Unlike the other analyzers, a reference brand is mandatory here: without it
	// the backend returns every KPI as null. The `searchID` shortcut bypasses the
	// schema, so these checks have to be explicit on both paths.
	const hasMainReference = !!mainSite.value || !!mainProvider.value
	const hasRequiredSite = !isProviderMode
		|| (Array.isArray(sites.value) ? !!sites.value.length : !!sites.value)

	return {
		onSubmit: onSubmit,
		isValid: ((isValid && !!reports.value) || !!searchID.value) && hasMainReference && hasRequiredSite,
		errors: errors
	}
}
